/**
 * §P.1 · Pagadores.
 *
 * **El pagador no es el alumno y no siempre es el tutor.** Es quien recibe el
 * recibo, y la remesa agrupa por él: una madre con tres hijos recibe **un** recibo
 * con cinco líneas.
 *
 * Lo que de verdad importa de esta entidad es `user_id`:
 *
 * > Es lo único que une el recibo con la app de familias: **sin él, el recibo
 * > existe y la madre no lo ve.**
 *
 * Y puede quedarse a `null` a propósito —una empresa que subvenciona a sus
 * empleados, un abuelo que paga y no quiere app—, así que no es obligatorio. Lo
 * que sí hace la pantalla es **decir cuáles están sueltos**, porque un pagador sin
 * cuenta es un recibo que nadie va a ver.
 */

export interface Pagador {
  readonly id: string;
  /** La cuenta de la app. `null` es legítimo, pero hay que verlo. */
  readonly user_id: string | null;
  readonly legal_name: string;
  readonly tax_id: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly address_line: string | null;
  readonly postal_code: string | null;
  readonly city: string | null;
  readonly country: string | null;
}

export interface PaginaDePagadores {
  readonly data: readonly Pagador[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

/** A quién cubre este pagador, con el estado de cada cuota. */
export interface AlumnoCubierto {
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly subscription_id: string | null;
  readonly status: string;
}

/** Sus últimos 24 recibos. **Céntimos enteros.** */
export interface ReciboDelPagador {
  readonly invoice_id: string;
  readonly period: string | null;
  readonly due_date: Date | string | null;
  readonly total_cents: number;
  readonly status: string;
}

/**
 * La ficha entera, en una llamada.
 *
 * «Las tres cosas en una llamada porque son una pantalla»: quien la abre está
 * resolviendo *esta familia llama porque no entiende su recibo*, y necesita ver a
 * la vez a quién cubre y qué se le ha cobrado.
 */
export interface FichaDePagador {
  readonly payer: Pagador;
  readonly students: readonly AlumnoCubierto[];
  readonly invoices: readonly ReciboDelPagador[];
}

export const sinCuenta = (p: Pagador): boolean => p.user_id === null;
